import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CircleNotch, Percent } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

const PRESETS = [0, 25, 50, 75, 100] as const;

interface SubInfo {
  sub_partida_id: number;
  codigo: string;
  nombre: string;
  pct_completado: number;
}

interface Props {
  /** Sub-partida sobre la que se captura % — null = cerrado. */
  sub: SubInfo | null;
  pending: boolean;
  onClose: () => void;
  onConfirmar: (pct: number) => void;
}

/**
 * Diálogo rápido para capturar % de una sub-partida sin navegar a otra
 * pantalla (réplica del modal del original que aparece desde el Kanban al
 * tocar el botón %).
 */
export function AvanceRapidoDialog({ sub, pending, onClose, onConfirmar }: Props) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (sub) setPct(Math.round(sub.pct_completado));
  }, [sub]);

  const abierto = sub !== null;

  function handleOpenChange(open: boolean) {
    if (!open && !pending) onClose();
  }

  return (
    <Dialog open={abierto} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-blue-600" weight="bold" aria-hidden="true" />
            Avance de la sub-partida
          </DialogTitle>
          <DialogDescription>
            {sub ? (
              <span>
                <span className="font-mono text-xs">{sub.codigo}</span> · {sub.nombre}
              </span>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-center">
            <span className="text-4xl font-bold tabular-nums text-blue-600">{pct}</span>
            <span className="text-2xl font-semibold text-blue-600">%</span>
          </div>

          {/* Presets */}
          <div className="grid grid-cols-5 gap-2">
            {PRESETS.map((v) => (
              <Button
                key={v}
                type="button"
                variant={pct === v ? 'default' : 'outline'}
                onClick={() => setPct(v)}
              >
                {v}%
              </Button>
            ))}
          </div>

          {/* Slider */}
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={pct}
            onChange={(e) => setPct(Number(e.target.value))}
            className="w-full accent-blue-600"
            aria-label="Porcentaje de avance"
          />

          <div>
            <Label htmlFor="rap-exacto" className="mb-1.5 block text-xs">
              Valor exacto
            </Label>
            <Input
              id="rap-exacto"
              type="number"
              min={0}
              max={100}
              value={pct}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isNaN(n)) setPct(Math.max(0, Math.min(100, n)));
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button onClick={() => onConfirmar(pct)} disabled={pending}>
            {pending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Guardando…
              </>
            ) : (
              'Guardar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
